import {
  CustomButton,
  Modal,
  SvgSuccessTick,
  SvgWarning,
  Tooltip,
} from '@ui-kit'
import React, { useState } from 'react'
import {
  MissingInformationModalPropsType,
  TaskResponseModalStateType,
} from '../../types/workflow.types'
import {
  addToaster,
  completeTaskByTaskIdApi,
  hideLoader,
  interruptWorkFlow,
  showLoader,
  updateWorkflowInstance,
  WorkflowProcessType,
} from '@app-store'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import { v4 as uuid } from 'uuid'
import { MISSING_INFO_CONSTANTS } from '../../constants/missingInfoModal.constants'
import { MoveToMIModal } from './WorkflowRightSplitTaskTable'
import { isSuperAdmin } from '../../utils/permission'
import { editEntityDataEmbeddedAPI } from '../../apis/entityData.api'
import { STRINGS } from '../../utils/strings'
type ModalActionType = 'PROCEED' | 'SUBMIT' | 'CLOSE' | 'MI_PROCEED'

const MissingInformationModal: React.FC<MissingInformationModalPropsType> = ({
  taskResponseModal,
  handleProceedClick,
  handleModalClose,
  showButtons = false,
  fetchTasklist,
  refetchWorkflow,
  definition,
  instance,
  resetTaskList,
}) => {
  const showQuickDenial =
    !!taskResponseModal?.workflow?.checkerResult?.quickDenialDetails?.denied
  const quickDenialMessages =
    taskResponseModal?.workflow?.checkerResult?.quickDenialDetails
      ?.quick_denial_message_map ?? {}
  const workflowInstanceSelector = useAppSelector(
    (state) => state?.workflowData?.workflowInstance
  )
  const caseData = useAppSelector((state) => state?.workflowData?.caseData)

  const workflowDefinitionSelector = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const workflowDefinition = definition || workflowDefinitionSelector
  const workflowInstance = instance || workflowInstanceSelector

  const programServices = useAppSelector(
    (state) => state?.clientPermissions?.programServices
  )

  const dispatch = useAppDispatch()

  // var to check eligiblity and mismatch conditions
  const result = taskResponseModal?.workflow?.checkerResult?.data?.result
  const matchStatus = {
    isMiMismatch: result === MISSING_INFO_CONSTANTS.MIMISMATCH,
    isEligibleMatch: result === MISSING_INFO_CONSTANTS.ELIGIBLE,
    isInEligibleMatch: result === MISSING_INFO_CONSTANTS.INELIGIBLE,
    isFundingStatusNull: result === MISSING_INFO_CONSTANTS.FUNDING_STATUS_NULL,
  }

  const checkMstchStatus = async () => {
    if (matchStatus.isMiMismatch) {
      setIsFollowUpTask({ isOpen: true })
    } else {
      await completeTask()
    }
  }

  const [isFollowUpTask, setIsFollowUpTask] = useState<{ isOpen: boolean }>({
    isOpen: false,
  })

  const refetchTasks = () => {
    const checkerData = taskResponseModal?.workflow?.checkerResult?.data
    if (
      checkerData?.copay_mismatch &&
      checkerData?.result &&
      checkerData?.result === MISSING_INFO_CONSTANTS.ELIGIBLE
    ) {
      fetchTasklist?.()
      resetTaskList?.()
    }
  }

  const OnClose = async () => {
    if (
      taskResponseModal?.workflow?.checkerResult?.valid ||
      taskResponseModal?.workflow?.checkerResult?.status ===
        MISSING_INFO_CONSTANTS.SUCCESS
    ) {
      const checkerData = taskResponseModal?.workflow?.checkerResult?.data
      if (taskResponseModal?.task?.identifier?.stopCurrentFlow) {
        try {
          dispatch(showLoader())
          const interruptResponse = await interruptWorkFlow({
            workflowId: workflowInstance?.id ?? '',
            signal: MISSING_INFO_CONSTANTS.STOPCURRENTFLOW,
          })

          if (interruptResponse?.data) {
            fetchTasklist?.()
            refetchWorkflow && refetchWorkflow()
          }
        } catch (err) {
          console.error(err)
        } finally {
          dispatch(hideLoader())
        }
      } else if (
        checkerData?.copay_mismatch &&
        checkerData?.result &&
        checkerData?.result === MISSING_INFO_CONSTANTS.ELIGIBLE
      ) {
        fetchTasklist?.()
        resetTaskList && resetTaskList()
      }
    } else {
      fetchTasklist?.()
    }
    handleModalClose()
  }

  const completeTask = async () => {
    const taskId =
      taskResponseModal?.workflow?.taskId?.toString() ??
      taskResponseModal?.task?.id?.toString()

    if (!taskId) return

    const forced = true
    const meta_data = {}

    try {
      dispatch(showLoader())
      const programId = caseData?.programs?.id ?? workflowDefinition?.program_id
      const serviceId = caseData?.services?.id ?? workflowDefinition?.service_id
      if (
        !isSuperAdmin() &&
        !programServices[programId ?? '']?.includes(serviceId ?? 0)
      ) {
        await editEntityDataEmbeddedAPI(
          workflowDefinition?.global_variables?.entity_name ?? '',
          workflowInstance?.case_id?.toString() ?? '',
          {
            entityName: workflowDefinition?.global_variables?.entity_name ?? '',
            users: {
              _state: 'REMOVED',
            },
          },
          ['users']
        )
      }
      const res = await completeTaskByTaskIdApi({
        taskId,
        meta_data,
        forced,
      })

      if (res.data) {
        dispatch(updateWorkflowInstance(res.data.instance))

        dispatch(
          addToaster({
            message: MISSING_INFO_CONSTANTS.TASK_SUCCESS_MSG,
            type: 'success',
            id: uuid(),
          })
        )

        OnClose()
      }
    } catch (err) {
      dispatch(
        addToaster({
          message: MISSING_INFO_CONSTANTS.TASK_ERROR_MSG,
          type: 'error',
          id: uuid(),
        })
      )
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const closeFollowupTask = () => {
    setIsFollowUpTask({ isOpen: false })
    OnClose()
  }

  const checkIfStatusIsSuccess = (
    taskResponseModal: TaskResponseModalStateType
  ) => {
    return (
      (taskResponseModal?.workflow?.preRequisiteExecuted &&
        taskResponseModal?.workflow?.checkerResult?.status !==
          MISSING_INFO_CONSTANTS.SUCCESS) ||
      !!taskResponseModal?.workflow?.checkerResult?.quickDenialDetails?.denied
    )
  }

  const getMissingInfoText = (
    taskResponseModal: TaskResponseModalStateType,
    workflowDefinition: WorkflowProcessType | null
  ) => {
    if (!checkIfStatusIsSuccess(taskResponseModal)) {
      return workflowDefinition?.definition?.processVariables
        ?.inherited_variables?.success_popup_text
    } else {
      return workflowDefinition?.definition?.processVariables
        ?.inherited_variables?.mi_popup_text
    }
  }

  const getStatusIcon = (taskResponseModal: TaskResponseModalStateType) =>
    checkIfStatusIsSuccess(taskResponseModal) ? (
      <SvgWarning />
    ) : (
      <SvgSuccessTick />
    )

  const onClickProceedMI = async () => {
    if (
      !workflowDefinition?.global_variables?.stepper_config?.action_buttons
        ?.missing_information?.signal
    )
      return
    try {
      dispatch(showLoader())
      const interruptResponse = await interruptWorkFlow({
        workflowId: workflowInstance?.id ?? '',
        signal:
          workflowDefinition?.global_variables?.stepper_config?.action_buttons
            ?.missing_information?.signal,
      })

      if (interruptResponse?.data) {
        fetchTasklist?.()
        refetchWorkflow && refetchWorkflow()
      }
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
      handleModalClose()
    }
  }

  const getMessageText = (taskResponseModal: TaskResponseModalStateType) =>
    checkIfStatusIsSuccess(taskResponseModal)
      ? 'Missing Information Found'
      : 'All Information Complete '

  const getStatusIconClassName = (
    taskResponseModal: TaskResponseModalStateType
  ) => (checkIfStatusIsSuccess(taskResponseModal) ? 'bg-error-100' : '')

  const flowText = (taskResponseModal: TaskResponseModalStateType) =>
    checkIfStatusIsSuccess(taskResponseModal)
      ? showQuickDenial
        ? STRINGS.PROCEED_TO_QD
        : STRINGS.PROCEED_TO_MI
      : workflowDefinition?.definition?.processVariables?.inherited_variables
          ?.success_popup_button_text

  const getFollowUpTask = () => {
    if (!isFollowUpTask.isOpen) {
      return (
        <div className="flex flex-col p-[0.87rem] w-[28.625rem] gap-2 my-[-1rem]">
          <div className="flex flex-col gap-3">
            {!matchStatus.isFundingStatusNull && (
              <div className="flex flex-col items-center gap-5">
                <div
                  className={`flex font-primary bg-error-100 font-medium text-sm items-center justify-center px-4 py-[0.5rem] rounded-full`}
                >
                  <SvgWarning className="mr-2" />
                  {MISSING_INFO_CONSTANTS.MISMATCH_TEXT}
                </div>
              </div>
            )}

            <div className="flex flex-col justify-center text-center items-center rounded max-h-[500px] overflow-auto font-primary font-regular text-sm">
              {taskResponseModal?.workflow?.checkerResult?.data?.copay_mismatch
                ? taskResponseModal?.workflow?.checkerResult?.data
                    ?.copay_mismatch
                : taskResponseModal?.workflow?.checkerResult?.messageMap
                    ?.mismatch}
            </div>
          </div>
          {!matchStatus.isFundingStatusNull && (
            <div className="flex justify-end items-center gap-[0.68rem] pt-3 border-t border-t-accent-100">
              <CustomButton onClick={OnClose}>
                {MISSING_INFO_CONSTANTS.NO}
              </CustomButton>

              <CustomButton onClick={checkMstchStatus} isFilled>
                {MISSING_INFO_CONSTANTS.YES}
              </CustomButton>
            </div>
          )}
        </div>
      )
    } else {
      return (
        <div className="flex flex-col p-3.5 w-[28.625rem] gap-2 my-[-1rem]">
          <MoveToMIModal
            refetchWorkflow={refetchWorkflow}
            recordId={workflowInstance?.case_id?.toString() ?? ''}
            closeFollowupTask={closeFollowupTask}
            isMiMismatch={matchStatus.isMiMismatch}
            taskID={taskResponseModal?.workflow?.taskId}
          />
        </div>
      )
    }
  }
  const getModalActionType = (): ModalActionType => {
    if (!taskResponseModal?.workflow?.checkerResult?.valid || showButtons)
      return 'PROCEED'
    if (taskResponseModal?.workflow?.taskId) return 'SUBMIT'
    if (taskResponseModal?.task?.identifier?.SHOW_MI_POPUP) {
      if (showQuickDenial) {
        return 'PROCEED'
      }
      return 'MI_PROCEED'
    }
    return 'CLOSE'
  }

  const handleCloseButtonClick = () => {
    const status = taskResponseModal?.workflow?.checkerResult?.status
    if (
      status === MISSING_INFO_CONSTANTS.SUCCESS &&
      // this key is also available true in our case so not making in fix so using forced key,
      //  which is added by us only for our flow, later we will make change in stopCurrentFlow
      // key to false
      // !taskResponseModal?.task?.identifier?.stopCurrentFlow
      taskResponseModal?.task?.identifier?.forced ===
        MISSING_INFO_CONSTANTS.FALSE
    ) {
      completeTask()
    } else {
      OnClose()
    }
  }

  const refetchCaseStatus = async () => {
    if (showQuickDenial) {
      setTimeout(() => {
        let count = 0
        const interval = setInterval(() => {
          if (count <= 3) {
            const ev = new CustomEvent('refetchthestatus', {
              detail: 'refetch',
              bubbles: true,
              cancelable: true,
            })
            window.dispatchEvent(ev)
            count++
          } else {
            clearInterval(interval)
          }
        }, 5000)
      }, 2000)
    }
  }
  const getButtonForModal = () => {
    const actionType = getModalActionType()
    switch (actionType) {
      case 'MI_PROCEED':
        return (
          <CustomButton isFilled onClick={onClickProceedMI}>
            {STRINGS.PROCEED_TO_MI}
          </CustomButton>
        )
      case 'PROCEED':
        return (
          <CustomButton
            className="w-full"
            isFilled
            onClick={() => {
              handleProceedClick()
              refetchCaseStatus()
            }}
          >
            {flowText(taskResponseModal)}
          </CustomButton>
        )
      case 'SUBMIT':
        return (
          <CustomButton onClick={completeTask} isFilled>
            {MISSING_INFO_CONSTANTS.SUBMIT}
          </CustomButton>
        )
      case 'CLOSE':
      default:
        return (
          <CustomButton onClick={handleCloseButtonClick} isFilled>
            {MISSING_INFO_CONSTANTS.CLOSE}
          </CustomButton>
        )
    }
  }

  return (
    <Modal
      isOpen={taskResponseModal?.isOpen}
      closeModal={() => {
        handleModalClose()
        refetchTasks()
      }}
    >
      {matchStatus.isEligibleMatch ||
      matchStatus.isInEligibleMatch ||
      matchStatus.isFundingStatusNull ||
      matchStatus.isMiMismatch ? (
        getFollowUpTask()
      ) : (
        <div className="flex flex-col p-[0.87rem] w-[28.625rem] gap-5">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col items-center gap-5">
              <div
                className={`flex items-center justify-center size-[3.6rem] rounded-full ${getStatusIconClassName(
                  taskResponseModal
                )}`}
              >
                {getStatusIcon(taskResponseModal)}
              </div>

              <div className="flex items-center gap-[0.5rem]">
                {!showQuickDenial &&
                checkIfStatusIsSuccess(taskResponseModal) ? (
                  <div className="flex items-center justify-center w-5 h-5 border rounded border-primary-500">
                    {checkIfStatusIsSuccess(taskResponseModal)
                      ? taskResponseModal?.workflow?.checkerResult?.messages
                          ?.length
                      : ''}
                  </div>
                ) : null}

                {!showQuickDenial && (
                  <span className="text-base font-medium">
                    {getMessageText(taskResponseModal)}
                  </span>
                )}
              </div>
              <span className="text-xs font-light leading-snug text-center block">
                {showQuickDenial
                  ? STRINGS.QD_POP_UP_HEADING
                  : getMissingInfoText(taskResponseModal, workflowDefinition)}
              </span>
            </div>
            {checkIfStatusIsSuccess(taskResponseModal) ? (
              <div className="flex flex-col p-3 border rounded max-h-[500px] overflow-auto">
                {!showQuickDenial ? (
                  <ul>
                    {taskResponseModal?.workflow?.checkerResult?.messages?.map(
                      (element, index) => (
                        <li
                          key={`${element}-${index}`}
                          className="list-none flex items-center before:content-['•'] before:mr-2 text-xs font-light leading-6"
                        >
                          {element}
                        </li>
                      )
                    )}
                  </ul>
                ) : (
                  <table
                    className="text-primary border-collapse border border-gray-200 w-[24.875rem]"
                    style={{
                      tableLayout: 'fixed',
                    }}
                  >
                    <thead>
                      <tr className="bg-secondary-25 border-b border-gray-200">
                        <th className="p-[0.625rem] text-left text-sm font-medium text-gray-800 w-[12.4375rem] h-[2.375rem]">
                          {STRINGS.QUICK_DENIAL}
                        </th>
                        <th className="p-[0.625rem] text-left text-sm font-medium text-gray-800 w-[12.4375rem] h-[2.375rem]">
                          {STRINGS.QUICK_DENIAL_REASON}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {Object.entries(quickDenialMessages).map(
                        ([key, value]: [string, string]) => (
                          <tr
                            key={key}
                            className="border-b border-gray-200 last:border-b-0"
                          >
                            <td className="p-[0.625rem] text-sm text-gray-700 w-[12.4375rem] h-[2.375rem]">
                              <Tooltip content={key} position="top">
                                <span className="overflow-hidden text-ellipsis whitespace-nowrap block">
                                  {key}
                                </span>
                              </Tooltip>
                            </td>
                            <td className="p-[0.625rem] text-sm text-gray-700 w-[12.4375rem] h-[2.375rem]">
                              <Tooltip content={value} position="top">
                                <span className="overflow-hidden text-ellipsis whitespace-nowrap block">
                                  {value}
                                </span>
                              </Tooltip>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            ) : null}
          </div>
          <div className="flex flex-col justify-center items-center gap-[0.68rem] w-[213px] mx-auto">
            {/* <CustomButton className="w-full" onClick={() => navigate(-1)}>
          Submit and Return to Intake
        </CustomButton> */}

            {getButtonForModal()}
          </div>
        </div>
      )}
    </Modal>
  )
}

export default MissingInformationModal
